import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useStreak } from "@/context/StreakContext";
import { PROMPT_BY_ID } from "@/lib/prompts";
import { CalendarRange, Flame } from "lucide-react";

const weekdayName = (index) => {
  // 0 = Monday, matching Python's weekday()
  const monday = new Date(Date.UTC(2024, 0, 1 + index, 12));
  return monday.toLocaleDateString(undefined, { weekday: "long" });
};

const dayLabel = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "narrow" });
  } catch {
    return "";
  }
};

export const PromptRecap = ({ custom = [], refreshKey = 0 }) => {
  const [recap, setRecap] = useState(null);
  const [days, setDays] = useState(() => Number(localStorage.getItem("cml_recap_days")) || 7);
  const { streak: appStreak } = useStreak();

  useEffect(() => {
    localStorage.setItem("cml_recap_days", String(days));
    api
      .get("/prompts/recap", { params: { days } })
      .then(({ data }) => setRecap(data))
      .catch((e) => console.error("could not load the recap", e));
  }, [refreshKey, days]);

  if (!recap) return null;

  // one number everywhere: the header's streak wins, the recap's own count is the fallback
  const streakDays = appStreak ? appStreak.current : recap.streak;
  const streakLive = appStreak ? appStreak.active_today : recap.streak_live;

  const name = (key) => {
    const id = key.replace(/^[a-z_]+:/, "");
    return PROMPT_BY_ID[id]?.label || custom.find((p) => p.id === id)?.label || id.replace(/_/g, " ");
  };
  const peak = Math.max(1, ...recap.by_day.map((d) => d.count));

  return (
    <div className="mb-4 rounded-3xl border border-white/10 bg-panel p-6" data-testid="prompt-recap">
      <div className="flex flex-wrap items-center gap-3">
        <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
          <CalendarRange className="h-4 w-4 text-brand" /> My {days === 7 ? "week" : "month"} with MiLu
        </p>
        {streakDays >= 1 && (
          <span
            data-testid="recap-streak"
            title={streakLive ? "Keep it going — same streak as the flame in the header" : "Chat with MiLu today to keep it alive"}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
              streakLive
                ? "border-orange-400/40 bg-orange-400/10 text-orange-300"
                : "border-white/10 text-ink-soft"
            }`}
          >
            <Flame className={`h-3.5 w-3.5 ${streakLive ? "streak-flicker" : ""}`} /> {streakDays}-day streak
          </span>
        )}
        <span className="ms-auto flex gap-1.5" data-testid="recap-range">
          {[7, 30].map((d) => (
            <button
              key={d}
              data-testid={`recap-range-${d}`}
              onClick={() => setDays(d)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                days === d ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
              }`}
            >
              {d} days
            </button>
          ))}
        </span>
      </div>

      {recap.total === 0 ? (
        <p className="mt-2 text-sm text-ink-soft" data-testid="prompt-recap-empty">
          Nothing yet this week — open a prompt and your recap starts filling in.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-ink-soft" data-testid="prompt-recap-total">
            {recap.total} {recap.total === 1 ? "prompt opened" : "prompts opened"} across {recap.kinds}{" "}
            {recap.kinds === 1 ? "tool" : "different tools"} in the last {recap.days} days.
          </p>

          {recap.insight && (
            <p className="mt-2 text-sm text-ink" data-testid="recap-insight">
              {recap.insight.on_weekday >= 2
                ? `You reach for ${name(recap.insight.prompt_key)} most on ${weekdayName(recap.insight.weekday)}s.`
                : recap.insight.kinds === 1
                  ? `${name(recap.insight.prompt_key)} is your one and only so far — try another from the board.`
                  : `${name(recap.insight.prompt_key)} is your favourite so far, and ${weekdayName(
                      recap.insight.weekday
                    )} was the day you used it.`}
            </p>
          )}

          <div className="mt-4 flex items-end gap-1" data-testid="prompt-recap-bars">
            {recap.by_day.map((d) => (
              <span key={d.day} className="flex flex-1 flex-col items-center gap-1.5" title={`${d.day}: ${d.count}`}>
                <span
                  style={{ height: `${Math.max(5, (d.count / peak) * 56)}px` }}
                  className={`w-full rounded-sm transition-all ${d.count ? "bg-brand" : "bg-white/10"}`}
                />
                {recap.by_day.length <= 10 && (
                  <span className="text-[10px] text-ink-soft">{dayLabel(d.day)}</span>
                )}
              </span>
            ))}
          </div>
          {recap.by_day.length > 10 && (
            <p className="mt-1.5 flex justify-between text-[10px] text-ink-soft">
              <span>{recap.from}</span>
              <span>today</span>
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2" data-testid="prompt-recap-top">
            {recap.top.map((t) => (
              <span
                key={t.prompt_key}
                className="rounded-full border border-brand/30 bg-brand/10 px-3 py-1.5 text-xs text-brand"
              >
                {name(t.prompt_key)} · {t.count}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
